import { inject, injectable, Store } from '@servicetitan/react-ioc';

import { makeObservable, observable, runInAction } from 'mobx';

import { Feature } from './feature.enum';
import { FeaturesApi } from './features.api';
import { MfaEnforcementPolicy } from '../identity-user-management/multi-factor-authentication/enums/enforcement-policy';
import { DocumentManagementMigrationStatus } from '../documents/documentmanagement-migration-status';
import { DashboardRequestQueueingLevel } from '../dashboards/dashboard-request-queueing/dashboard-request-queueing-level';

export type Features = {
    [Feature.AdjustableCapacityPlanning]: boolean;
    [Feature.AdjustableCapacityPlanningEnhancedLogic]: boolean;
    [Feature.AbilityToSetBillingPreference]: boolean;
    [Feature.AcpRules]: boolean;
    [Feature.Alerts]: boolean;
    [Feature.AllowWorldpayIntegration]: boolean;
    [Feature.ApplyAllFiltersToDispatchBoardMap]: boolean;
    [Feature.AreLineItemsEnabled]: boolean;
    [Feature.AllowExportPermission]: boolean;
    [Feature.CapacityPlanningByJobCount]: boolean;
    [Feature.CustomerNotificationRealtimeTechTracking]: boolean;
    [Feature.DashboardKpi20]: boolean;
    [Feature.EnablePayrollPersistence]: boolean;
    [Feature.DisplayUnassignedJobs]: boolean;
    [Feature.DoNotShowRescheduleMessageForDoneForNow]: boolean;
    [Feature.EnableAdpIntegration]: boolean;
    [Feature.EnableAdvancePayments]: boolean;
    [Feature.EnableAdvancePaymentFeature]: boolean;
    [Feature.EnableAllisonDirectMailingService]: boolean;
    [Feature.EnableApiV2]: boolean;
    [Feature.ManageIntegrationEnvironment]: boolean;
    [Feature.EnableAuditTrails]: boolean;
    [Feature.EnableAuditTrailsForFlexibleTimekeeping]: boolean;
    [Feature.EnableAutoGlass]: boolean;
    [Feature.NewAutoGlassFlowEnabled]: boolean;
    [Feature.EnableWindshieldProtectionPlan]: boolean;
    [Feature.EnableHoverIntegration]: boolean;
    [Feature.EnableRoofingMeasurementIntegrations]: boolean;
    [Feature.EnableAvalaraIntegration]: boolean;
    [Feature.EnableAvalaraCacheIntegration]: boolean;
    [Feature.EnableAvalaraRequestResponseCache]: boolean;
    [Feature.EnableBudgeting]: boolean;
    [Feature.EnableBusinessUnitsByZipCodeFilter]: boolean;
    [Feature.EnableCallScripts]: boolean;
    [Feature.EnableCallScriptBuilder]: boolean;
    [Feature.EnableCampaignCategoriesListEditing]: boolean;
    [Feature.EnableCapacityPlanning]: boolean;
    [Feature.EnableCarrierLeadsIntegration]: boolean;
    [Feature.AllowEditAggregatesInReportingGrid]: boolean;
    [Feature.EnableContentPortal]: boolean;
    [Feature.EnableCoburnIntegration]: boolean;
    [Feature.EnableCustomArrivalWindowsOnJobs]: boolean;
    [Feature.EnableCustomerLeadsForFollowUps]: boolean;
    [Feature.EnableCustomerPortal]: boolean;
    [Feature.EnableCustomerPortalNew]: boolean;
    [Feature.EnableCustomerPortalUserMigration]: boolean;
    [Feature.EnableCustomOpportunityStatus]: boolean;
    [Feature.EnableCustomPayrollBuilder]: boolean;
    [Feature.EnableDynamicPricing]: boolean;
    [Feature.EnableDynamicPricingForBusinessUnits]: boolean;
    [Feature.EnableFlatGlass]: boolean;
    [Feature.EnableFlexibleOvertime]: boolean;
    [Feature.EnableEmbeddedDialpadPhone]: boolean;
    [Feature.EnableExternalSearch]: boolean;
    [Feature.EnableGlobalSearchButton]: boolean;
    [Feature.SearchBarMFEUrl]: string;
    [Feature.EnableFinanceitIntegration]: boolean;
    [Feature.FacebookIntegration]: boolean;
    [Feature.FinanceitCustomRules]: boolean;
    [Feature.EnableGeneralLedgerAccounts]: boolean;
    [Feature.EnableGreenskyIntegration]: boolean;
    [Feature.EnableCustomGreenSkyFinancingRules]: boolean;
    [Feature.EnableHvacDashboard]: boolean;
    [Feature.EnableIntacctIntegration]: boolean;
    [Feature.EnableJobBookingTargets]: boolean;
    [Feature.EnableJobValuePredictor]: boolean;
    [Feature.EnableRevenuePredictor]: boolean;
    [Feature.EnableRevenueRecognitionEnhancements]: boolean;
    [Feature.EnableStraightLineRevenueRecognition]: boolean;
    [Feature.EnableServiceAgreementsRecurringVisits]: boolean;
    [Feature.EnableServiceAgreementsRecurringVisitsOnVisitTemplates]: boolean;
    [Feature.EnableServiceAgreementsPerVisitPricing]: boolean;
    [Feature.EnableServiceAgreementEsign]: boolean;
    [Feature.EnableLoyaltyProgram]: boolean;
    [Feature.EnableOfficeEmployeeTimesheets]: boolean;
    [Feature.EnableNewNjeUI]: boolean;
    [Feature.EnableNewTimesheetEditWorkflow]: boolean;
    [Feature.EnableOnboardingJumpStart]: boolean;
    [Feature.EnableOnboardingWorkflow]: number;
    [Feature.EnableTitanAdvisorPrepareToLaunch]: boolean;
    [Feature.EnableTitanAdvisorPrepareToLaunchWorkstreams]: boolean;
    [Feature.EnablePayrollCodes]: boolean;
    [Feature.EnableLaborTypes]: boolean;
    [Feature.EnableMembershipProfitabilityKpis]: boolean;
    [Feature.EnablePricebookConfigurator]: boolean;
    [Feature.EnablePriceModifiers]: boolean;
    [Feature.EnableSipForwarding]: boolean;
    [Feature.EnablePurchasingModule]: boolean;
    [Feature.EnableInventoryModule]: boolean;
    [Feature.EnableBulkInventoryActions]: boolean;
    [Feature.DisplayPercentOfRevenueColumnForInventory]: boolean;
    [Feature.IsSerializedTrackingEnabled]: boolean;
    [Feature.IsSerialNumbersBulkUploadEnabled]: boolean;
    [Feature.EnablePayablesModule]: boolean;
    [Feature.EnablePayables]: boolean;
    [Feature.EnablePayables3WayMatch]: boolean;
    [Feature.EnablePayablesAutomatedInbox]: boolean;
    [Feature.PayablesAutomatedInboxEmail]: string;
    [Feature.EnablePayablesLegacyVendor]: boolean;
    [Feature.EnablePayByBankOnlinePayments]: boolean;
    [Feature.EnableRefunds]: boolean;
    [Feature.EnableServiceTitanPhonesUser]: boolean;
    [Feature.EnablePhonesProCallContinuityToggle]: boolean;
    [Feature.EnableCallRecordingEnhancements]: boolean;
    [Feature.EnableContactCenterPro]: boolean;
    [Feature.EnableSkillsForTechniciansAndJobTypes]: boolean;
    [Feature.EnableSmartDispatch]: boolean;
    [Feature.EnableDispatchProDemoDataGenerator]: boolean;
    [Feature.DispatchProSettingsStatus]: number;
    [Feature.EnableDispatchProZeroOnboarding]: boolean;
    [Feature.EnableSalesFactorByJobType]: boolean;
    [Feature.UseJobValuePredictorV2]: boolean;
    [Feature.EnableStripeBillingACH]: boolean;
    [Feature.EnableStripeBillingCreditCard]: boolean;
    [Feature.EnableTaskManagement]: boolean;
    [Feature.EnableTechnicianClockInAndClockOut]: boolean;
    [Feature.DispatchBoardShowCheckedInForTodayOnly]: boolean;
    [Feature.DispatchCenterMicroFrontendUrl]: string;
    [Feature.EnableTimeSlots]: boolean;
    [Feature.EnableRescheduledReasonSurvey]: boolean;
    [Feature.EnableTitanAdvisor]: boolean;
    [Feature.EnableTitanAdvisorTrainingMode]: boolean;
    [Feature.TitanAdvisorUrl]: string;
    [Feature.EnableTurnsIntegration]: boolean;
    [Feature.EnableSearsCardProcessingIntegration]: boolean;
    [Feature.EnableServiceFinanceIntegration]: boolean;
    [Feature.EnableSMSMarketingCampaigns]: boolean;
    [Feature.FmaTaskingUx]: boolean;
    [Feature.SmsMarketingOptInViaLink]: boolean;
    [Feature.SmsMarketingOptInFormIframe]: boolean;
    [Feature.EnableGoodleapIntegration]: boolean;
    [Feature.EnableWellsFargoIntegration]: boolean;
    [Feature.EnableSynchronyIntegration]: boolean;
    [Feature.EnableOnlineEstimates]: boolean;
    [Feature.EnableFinancingService]: boolean;
    [Feature.EnableFinancingPreQualifications]: boolean;
    [Feature.EnableFinancingApplicationManagement]: boolean;
    [Feature.EnableVendorContentMatching]: boolean;
    [Feature.EnablePurchaseHistoryUpload]: boolean;
    [Feature.EnableVendorIntegrations]: boolean;
    [Feature.EnableJohnstoneIntegration]: boolean;
    [Feature.EnableTraneIntegration]: boolean;
    [Feature.EnableWinSupplyIntegration]: boolean;
    [Feature.EnableAprSupplyIntegration]: boolean;
    [Feature.EnableFergusonIntegration]: boolean;
    [Feature.EnableReeceIntegration]: boolean;
    [Feature.EnableFamousSupplyIntegration]: boolean;
    [Feature.EnableHajocaCoronaIntegration]: boolean;
    [Feature.EnableStandardPlumbingIntegration]: boolean;
    [Feature.EnableRobertsonHeatingIntegration]: boolean;
    [Feature.EnableSrsDistributionIntegration]: boolean;
    [Feature.EnableBeaconIntegration]: boolean;
    [Feature.EnableLennoxIntegration]: boolean;
    [Feature.EnableAbcSupplyIntegration]: boolean;
    [Feature.EnableAceHardwareIntegration]: boolean;
    [Feature.EnableCopperfieldIntegration]: boolean;
    [Feature.EnableSearchVendorCatalog]: boolean;
    [Feature.UseAccountMappingV2]: boolean;
    [Feature.EnableMultipleAccountIntegration]: boolean;
    [Feature.UseSkuExternalIdAsVendorPartNumberOnPurchaseOrder]: boolean;
    [Feature.EnableViewBillingOrUpdateCreditCardPermission]: boolean;
    [Feature.EmployeeBusinessUnitFiltering]: boolean;
    [Feature.ExternalReportingSettingsManagement]: boolean;
    [Feature.DashboardPerformanceOptimization]: boolean;
    [Feature.DashboardRequestQueueingLevel]: DashboardRequestQueueingLevel;
    [Feature.LeadsIntegration]: boolean;
    [Feature.GlobalJobTypeMappings]: boolean;
    [Feature.EnableGoogleLocalServices]: boolean;
    [Feature.WebSchedulerWidgetInstanceCountLimit]: number;
    [Feature.EnableWebSchedulerJobConversion]: boolean;
    [Feature.EnableNewWebSchedulerWidget]: boolean;
    [Feature.FormsEnabled]: boolean;
    [Feature.NexstarEdition]: boolean;
    [Feature.PraxisEdition]: boolean;
    [Feature.SnaEdition]: boolean;
    [Feature.SrtEdition]: boolean;
    [Feature.TorchEdition]: boolean;
    [Feature.DaikinEditionLogo]: boolean;
    [Feature.McaaEdition]: boolean;
    [Feature.ReadOnlyModeForDashboards]: boolean;
    [Feature.RestrictCreationOfKpiAndVisualizationModules]: boolean;
    [Feature.NavigateToCampaignDetailsPageFromModularDashboardMarketingScorecards]: boolean;
    [Feature.EnableBusinessStatusDashboardSettings]: boolean;
    [Feature.DashboardEnableNps]: boolean;
    [Feature.HideEquipmentTags]: boolean;
    [Feature.IntacctIdPrefix]: string;
    [Feature.IsConfigurablePayroll]: boolean;
    [Feature.IdealBookingPercentages]: boolean;
    [Feature.IncludeDriveTimeToJobDuration]: boolean;
    [Feature.IsDialpadEnabled]: boolean;
    [Feature.IsInventoryModuleEnabled]: boolean;
    [Feature.InventoryTransfersBetweenTenants]: boolean;
    [Feature.IsPayrollLite]: boolean;
    [Feature.IsSerializedTrackingEnabled]: boolean;
    [Feature.IsDualTrackingEnabled]: boolean;
    [Feature.IsPurchasingModuleEnabled]: boolean;
    [Feature.EditOldTransactions]: boolean;
    [Feature.IsOldInventory]: boolean;
    [Feature.IsQbExportEnabled]: boolean;
    [Feature.JobsWithAppointments]: boolean;
    [Feature.Marketing]: boolean;
    [Feature.MarketingAnalytics]: boolean;
    [Feature.AutoDni]: boolean;
    [Feature.HideMarketingUpsellBanners]: boolean;
    [Feature.EnableDefaultCampaignsFeature]: boolean;
    [Feature.ShowBuFilterInMarketingAnalytics]: boolean;
    [Feature.MarketingCovid]: boolean;
    [Feature.MarketingFree]: boolean;
    [Feature.FlowcardCampaigns]: boolean;
    [Feature.QuickstartCampaigns]: boolean;
    [Feature.FlowcardSmsCampaigns]: boolean;
    [Feature.EmailSubscriptionManagement]: boolean;
    [Feature.EditAndResumeStoppedCampaigns]: boolean;
    [Feature.SuppressionDistributedCapabilities]: boolean;
    [Feature.DirectMail]: boolean;
    [Feature.Acquisition]: boolean;
    [Feature.SenderDomain]: string;
    [Feature.EnableCustomFieldsInAudienceBuilder]: boolean;
    [Feature.Memberships]: boolean;
    [Feature.NavisionPayrollIntegration]: boolean;
    [Feature.OnlineBooking]: boolean;
    [Feature.Payroll]: boolean;
    [Feature.QuickbooksDesktopEnabled]: boolean;
    [Feature.QbdSyncAsYouGoEnabled]: boolean;
    [Feature.QboExportEnabled]: boolean;
    [Feature.QboExportUseSyncPlatform]: boolean;
    [Feature.XeroExportEnabled]: boolean;
    [Feature.QbdRebuildEnable]: boolean;
    [Feature.Qbd20Staging]: boolean;
    [Feature.ReportingBusinessUnitUserRestrictions]: boolean;
    [Feature.ReportingAllowAdminsAccessToAllCustomReports]: boolean;
    [Feature.ReportingEnableDashboardGridFiltersFromURL]: boolean;
    [Feature.ReportingLibraryEnabled]: boolean;
    [Feature.ReportingJoinsBetweenDatasetsEnabled]: boolean;
    [Feature.Reports20]: boolean;
    [Feature.Reports20Redesign]: boolean;
    [Feature.Reports20ReportEngine]: boolean;
    [Feature.Reports20Scheduler]: boolean;
    [Feature.BenchmarkReportsLibraryEnabled]: boolean;
    [Feature.SearsIdNumbers]: boolean;
    [Feature.SearsLimitFranchiseeServiceArea]: boolean;
    [Feature.ServiceAgreements]: boolean;
    [Feature.ServiceAgreementVisitTemplates]: boolean;
    [Feature.UseTakeoverInScopeOfWork]: boolean;
    [Feature.ShowAuditTrailsToUsers]: boolean;
    [Feature.ShowPartnerPortalUserAsLegalEmployee]: boolean;
    [Feature.ShowTimeZoneOffsetFromTenant]: boolean;
    [Feature.TechnicianPricebookCategoryAccess]: boolean;
    [Feature.TechnicianTimesheets]: boolean;
    [Feature.TenantPackage]: number;
    [Feature.StandardReporting]: boolean;
    [Feature.UseTenantTimeZone]: boolean;
    [Feature.EnableMultipleTimeZones]: boolean;
    [Feature.EnablePaymentTerms]: boolean;
    [Feature.ProgressBilling]: boolean;
    [Feature.BudgetVsActualEnabled]: boolean;
    [Feature.AdvancedOverridesForProjectLabels]: boolean;
    [Feature.EnableCustomOutboundCallerId]: boolean;
    [Feature.EnableDefaultCallType]: boolean;
    [Feature.EnableDeferredRevenueGuardRails]: boolean;
    [Feature.EnableSipNumbers]: boolean;
    [Feature.HideLegacyReports]: boolean;
    [Feature.EnableBulkBookingOfUpcomingAppointments]: boolean;
    [Feature.EnablePricebookStagingArea]: boolean;
    [Feature.EnableCounterSales]: boolean;
    [Feature.JobCounter]: boolean;
    [Feature.Reputation]: boolean;
    [Feature.TechnicianAvailability]: boolean;
    [Feature.NewMarketsRecurringServices]: boolean;
    [Feature.ArrivalWindowsOnRecurringServiceTypes]: boolean;
    [Feature.EnableChargeablePOLineItems]: boolean;
    [Feature.EnableTimeAndMaterialsService]: boolean;
    [Feature.PasswordExpirationReminder]: number;
    [Feature.CapacityReportEnabled]: boolean;
    [Feature.TechnicianStatusBoard]: boolean;
    [Feature.IsReplenishmentEnabled]: boolean;
    [Feature.EnablePricingEngine]: boolean;
    [Feature.UserVendorQuickbooksItemForPurchaseOrders]: boolean;
    [Feature.EnableLocationTagColumnAndFilterForUpcomingAppointments]: boolean;
    [Feature.EnableMembershipRenewalProcessing]: boolean;
    [Feature.EnableMultipleBillingDurationCombinations]: boolean;
    [Feature.MembershipBillingPeriodOnPrintedInvoices]: boolean;
    [Feature.EnableInvoicePrintNewDesign]: boolean;
    [Feature.EnableTaskNameOnInvoicePrintNewDesign]: boolean;
    [Feature.IsBinTrackingEnabled]: boolean;
    [Feature.RoutingVisualization]: boolean;
    [Feature.EnablePointOfSale]: boolean;
    [Feature.EnableSmartDispatch]: boolean;
    [Feature.PointOfSalesMobileApp]: boolean;
    [Feature.SdTagPrioritization]: boolean;
    [Feature.MarketingAds]: boolean;
    [Feature.MarketingAdsCopilot]: boolean;
    [Feature.MarketingAdsMultiTouchAttribution]: boolean;
    [Feature.MarketingPlanningTool]: boolean;
    [Feature.MarketingProReputation]: boolean;
    [Feature.AutopilotOnboarding]: boolean;
    [Feature.AutopilotReporting]: boolean;
    [Feature.Text2ChatWidgetCountConfig]: number;
    [Feature.Text2ChatWidget]: boolean;
    [Feature.RecurringServiceTemplateFollowingYearsEditable]: boolean;
    [Feature.RecurringServicesViaSms]: boolean;
    [Feature.UnsoldEstimatesSMSCampaignFeature]: boolean;
    [Feature.MissedCallCampaignSMSCampaignFeature]: boolean;
    [Feature.ReputationLocationCountConfig]: number;
    [Feature.QuickbooksUseInvoiceTax]: boolean;
    [Feature.SmartDispatchDaysToPlan]: number;
    [Feature.SmartProposals]: boolean;
    [Feature.IsCaliforniaCustomInvoice]: boolean;
    [Feature.EnableCaliforniaCompliantContract]: boolean;
    [Feature.UseMembershipPricingTable]: boolean;
    [Feature.EnableMaterialsCharge]: boolean;
    [Feature.EnableMaterialsMarkupTable]: boolean;
    [Feature.EnableMaterialsMarkupTablePropertyHasCorrectValue]: boolean;
    [Feature.OnTheFlyMaterialPurchaseOrderMarkup]: boolean;
    [Feature.EnableOpportunitiesOnProjects]: boolean;
    [Feature.EnableOpportunitiesOnLocations]: boolean;
    [Feature.EnableProjectPortfolio]: boolean;
    [Feature.CommPlusDemo]: boolean;
    [Feature.EnableCommHub]: boolean;
    [Feature.CommHubUiProjectAndJob]: boolean;
    [Feature.EnableNewPhase3ContactManagement]: boolean;
    [Feature.RequireEmailAndPhoneDuringCallBooking]: boolean;
    [Feature.EnableWashingtonStateSalesTaxZones]: boolean;
    [Feature.EnableStcrm]: boolean;
    [Feature.PropertyDataWidget]: boolean;
    [Feature.EnableVisualIvr]: boolean;
    [Feature.HelpWidgetSupportWorkingHours]: string;
    [Feature.ShowHelpWidgetAskCommunityButton]: boolean;
    [Feature.RequisitionCloseout]: boolean;
    [Feature.EnableHelpWidgetOfChatQueuePosition]: boolean;
    [Feature.EnableCallsToTechnicians]: boolean;
    [Feature.EnableSmartSchedule]: boolean;
    [Feature.MaxSmartScheduleRangeInWeeks]: number;
    [Feature.PreventSelectingNonMatchingBusinessUnits]: boolean;
    [Feature.AllowPricebookItemsToBeAssignedToBusinessUnits]: boolean;
    [Feature.AllowSettingTaxStatusByInvoiceItem]: boolean;
    [Feature.MultipleInvoiceEstimatePrintOptionsEnabled]: boolean;
    [Feature.IsMaterialsChargeableByDefault]: boolean;
    [Feature.DashboardEnableNps]: boolean;
    [Feature.DontAutomaticallyCreateBills]: boolean;
    [Feature.IsInventoryTrackingStarted]: boolean;
    [Feature.AllowNegativeQuantityOnHand]: boolean;
    [Feature.AllowCopyingPoItemsToInvoice]: boolean;
    [Feature.InventoryBeginningDate]: string | undefined;
    [Feature.EnablePayrollPro]: boolean;
    [Feature.EnablePayrollPtoManagement]: boolean;
    [Feature.EnableTimesheetActivities]: boolean;
    [Feature.ConnectFlexibleTimekeepingToContinuousTimekeeping]: boolean;
    [Feature.EnableOngoingTimeEntryGuardrails]: boolean;
    [Feature.AllowPricebookItemsToBeAssignedToBusinessUnits]: boolean;
    [Feature.EquipmentPageRedesign24]: boolean;
    [Feature.EquipmentFindings]: boolean;
    [Feature.IsRefundWorkflowEnabled]: boolean;
    [Feature.ItemGroupsEnabled]: boolean;
    [Feature.AccountingIntegrationEnabled]: boolean;
    [Feature.EnableMultipleTaxRates]: boolean;
    [Feature.LiveEvents]: boolean;
    [Feature.HideGLAccountsonMaterials]: boolean;
    [Feature.VendorDocumentNumberRequired]: boolean;
    [Feature.IsSubAccountEnabled]: boolean;
    [Feature.EnableTitanExchange]: boolean;
    [Feature.MassReschedule]: boolean;
    [Feature.SaasquatchEnabled]: boolean;
    [Feature.IsRateSheetsEnabled]: boolean;
    [Feature.EnableAutomaticCalculationForCspLabor]: boolean;
    [Feature.EnableTitanExchange]: boolean;
    [Feature.SetPurchaseOrderNumberUsingJobNumber]: boolean;
    [Feature.HideAddPurchaseOrderOnPostedAndExportedInvoices]: boolean;
    [Feature.IsPayrollV12]: boolean;
    [Feature.IsPayrollV13]: boolean;
    [Feature.IsPayrollV15]: boolean;
    [Feature.IsPayrollV17]: boolean;
    [Feature.IsPayrollV18]: boolean;
    [Feature.HideDoneForNowGhostBubbles]: boolean;
    [Feature.ProjectSingleInvoiceView]: boolean;
    [Feature.EnableCustomPayrollFields]: boolean;
    [Feature.EnableTimesheetExportReportForUnionDues]: boolean;
    [Feature.EnableAdpIntegration]: boolean;
    [Feature.DisplayAnthonyPhcReportEnhancement]: boolean;
    [Feature.NavisionPayrollIntegration]: boolean;
    [Feature.ColorCodeScheduleBoardByTechnician]: boolean;
    [Feature.IsManagedTechPlan]: boolean;
    [Feature.IsOffServicePlan]: boolean;
    [Feature.MTLimiterEnabled]: boolean;
    [Feature.EnableCustomTechnicianOutboundCallerId]: boolean;
    [Feature.QuickbooksSingleWarehouseMode]: boolean;
    [Feature.EnableTechnicianLicenseNumber]: boolean;
    [Feature.DisplayPayrollReviewSignOff]: boolean;
    [Feature.EnableAppliance]: boolean;
    [Feature.EnableSecondChanceLeads]: boolean;
    [Feature.EnableSecondChanceLeadsPLG]: boolean;
    [Feature.HighCampaignCountUX]: number;
    [Feature.EnableEquipmentSystems]: boolean;
    [Feature.EnableSmartDispatchManualRun]: boolean;
    [Feature.ChatCenter]: boolean;
    [Feature.EnableTxnHub]: boolean;
    [Feature.EnableDTEInvoicePrint]: boolean;
    [Feature.EnableMapBasedRouting]: boolean;
    [Feature.EnableRoutingMFE]: boolean;
    [Feature.RoutingMfeUrl]: string;
    [Feature.EnableSTPaymentAutoBatching]: boolean;
    [Feature.LineItemsOnPurchaseOrders]: boolean;
    [Feature.EnableInventoryReplenishment]: boolean;
    [Feature.EnableTitanIntelligenceDataSharing]: boolean;
    [Feature.MarketingDirectMailCostDropDate]: Date;
    [Feature.EnableMarketingStandalone]: boolean;
    [Feature.EnableMarketingStandaloneContentManagementTitanExchange]: boolean;
    [Feature.EmailTemplatesDefaultToTenantCreated]: boolean;
    [Feature.StandaloneIntegration]: boolean;
    [Feature.EnableUpdatedDirectMailSizing]: boolean;
    [Feature.EnableUpdatedFollowUpsRecurringServiceEventsTable]: boolean;
    [Feature.EnableJournalEntryWorkflow]: boolean;
    [Feature.EnableNewAutoBatching]: boolean;
    [Feature.EnableAccountingPeriods]: boolean;
    [Feature.EnableJobDetailsFlyout]: boolean;
    [Feature.JefAdditionalInfoTab]: boolean;
    [Feature.EnableDispatchCenter]: boolean;
    [Feature.EnableDispatchEntityChangeDetection]: boolean;
    [Feature.FleetProIntegration]: boolean;
    [Feature.FleetProEnableGpsTracking]: boolean;
    [Feature.EnableGpsTimekeepingIntegration]: boolean;
    [Feature.ShowCompanyNameOnNavigation]: boolean;
    [Feature.TechRouteOptimizationSolver]: string;
    [Feature.AllowChangingCustomerForAdjustmentInvoices]: boolean;
    [Feature.IsRequisitionEnabled]: boolean;
    [Feature.IsInventoryEnabled]: boolean;
    [Feature.IsManagedInstallerEnabled]: boolean;
    [Feature.LimitJobsAndLocationsWithinZones]: boolean;
    [Feature.EnableRequisitionWorkflowForServiceJob]: boolean;
    [Feature.WarrantyPartsTracking]: boolean;
    [Feature.ProjectInvoice]: boolean;
    [Feature.PreventCancellationOfJobsWithInvoiceItems]: boolean;
    [Feature.EnableSublocations]: boolean;
    [Feature.StagingPicklists]: boolean;
    [Feature.EnableHelpWidgetMfe]: boolean;
    [Feature.EnableDocumentTemplateManager]: boolean;
    [Feature.EnableAfpEditPrice]: boolean;
    [Feature.EnableLeadsOnCallScreen]: boolean;
    [Feature.TreatV1AndV2BookingTheSame]: boolean;
    [Feature.EnableNewFiltersOnDailyDispatchBoard]: boolean;
    [Feature.DivideDispatchBoardByTeam]: boolean;
    [Feature.OverrideUrlForSmartDispatchTroubleshooter]: string;
    [Feature.EnableDispatchProSkillGeneration]: boolean;
    [Feature.DispatchAppointmentLoadOnDemand]: boolean;
    [Feature.CommittedCostsInJobCosting]: boolean;
    [Feature.EnableLeadsOnCallScreen]: boolean;
    [Feature.ConsignmentInventory]: boolean;
    [Feature.IsConsignmentInventoryTrackingEnabled]: boolean;
    [Feature.EnableFullStorySessionRecording]: boolean;
    [Feature.EnableNewIntacctPaymentApi]: boolean;
    [Feature.VendorDocumentNumberUnique]: boolean;
    [Feature.BudgetVsActualGLView]: boolean;
    [Feature.EnableSeparateAuthorizationParagraphForEstimate]: boolean;
    [Feature.EnableWorkspaces]: boolean;
    [Feature.EnableAcctChatBot]: boolean;
    [Feature.GenAiAccounting]: boolean;
    [Feature.EnableFeatureConfigurations]: boolean;
    [Feature.OcrEnabled]: boolean;
    [Feature.EnableConfigurableServices]: boolean;
    [Feature.Enable6X9DirectMailDesign]: boolean;
    [Feature.ReserveWithGoogle]: boolean;
    [Feature.ServiceChannelIntegration]: boolean;
    [Feature.SchedulingPro]: boolean;
    [Feature.SchedulingProDemo]: boolean;
    [Feature.OverrideManagedTechServicePlanRulesForAppointmentAndJobs]: boolean;
    [Feature.OverrideUrlForDispatchProSettings]: string;
    [Feature.EnableCommercialRebranding]: boolean;
    [Feature.DisplayMemberSavingsOnInvoices]: boolean;
    [Feature.EnableSupportInternationalDiplomaticBillingAddresses]: boolean;
    [Feature.SetLocationCoordsManually]: boolean;
    [Feature.MatchLocationTaxableToCustomerTaxable]: boolean;
    [Feature.LockTransactionsOnCompletedProjects]: boolean;
    [Feature.InternalExternalCommunicationEnabled]: boolean;
    [Feature.InternalCommunicationEnabled]: boolean;
    [Feature.ExternalCommunicationEnabled]: boolean;
    [Feature.EnableTimeInProjectStatus]: boolean;
    [Feature.EnableProjectGroups]: boolean;
    [Feature.EnableNewTaskManagementUI]: boolean;
    [Feature.DispatchCenterBottomPanel]: boolean;
    [Feature.DispatchCenterJefRewrite]: boolean;
    [Feature.DispatchCenterJobsTray]: boolean;
    [Feature.CanEnableCommercialNav]: boolean;
    [Feature.CanEnableConstructionNav]: boolean;
    [Feature.LeftSidedGlobalNav]: string | null;
    [Feature.EnableCommercialBilling]: boolean;
    [Feature.EnableConsolidatedInvoices]: boolean;
    [Feature.OverrideUrlForDispatchProJobDetailsMfe]: string;
    [Feature.IsWeightedAverageCostEnabled]: boolean;
    [Feature.PurchaseOrderApprovalField]: number;
    [Feature.ForceCustomerTypeSelection]: boolean;
    [Feature.EstimateTemplateImportExport]: boolean;
    [Feature.ProposalBuilder]: boolean;
    [Feature.SpecBasedTemplatesEnabled]: boolean;
    [Feature.EstimateDocumentTemplateEngine]: boolean;
    [Feature.EnableRestockingFeeOnVendorCredit]: boolean;
    [Feature.EnhancedBinTracking]: boolean;
    [Feature.EnableDispatchProTechnicianRankingSettings]: boolean;
    [Feature.AddEstimateToExistingJob]: boolean;
    [Feature.EnableAutoRefreshOnDailyDispatchBoard]: boolean;
    [Feature.EnableCreditRebillForClaimAdjustments]: boolean;
    [Feature.SmsMarketingRegistration]: boolean;
    [Feature.EnableShopNavigation]: boolean;
    [Feature.EstimateApprovalWorkflowEnabled]: boolean;
    [Feature.EnableTollFreeNumberVerificationStatus]: boolean;
    [Feature.EnableTcrCampaignDissassociation]: boolean;
    [Feature.EnableTcrCampaignIdColumn]: boolean;
    [Feature.DisableDispatchBoardViewTechnicianRoute]: boolean;
    [Feature.EnableBulkEmployeesAction]: boolean;
    [Feature.EnableFinancingDashboard]: boolean;
    [Feature.ReviewMatchingConfidenceLevel]: number;
    [Feature.EnableTeamsManagement]: boolean;
    [Feature.ItemRequisitions]: boolean;
    [Feature.ItemRequisitionDateNeededByType]: number;
    [Feature.AcpImprovedWindowCalculations]: boolean;
    [Feature.AcpDSTImprovements]: boolean;
    [Feature.MapLocationSeparationValue]: string;
    [Feature.EnableACPWindowsToBeMarkedAsClosed]: boolean;
    [Feature.DisableAzureActiveDirectorySync]: boolean;
    [Feature.EnableInvoiceSmsTextLink]: boolean;
    [Feature.EnableRevampedARManagement]: boolean;
    [Feature.IsMobileStorageEnabled]: boolean;
    [Feature.MobileStorage]: boolean;
    [Feature.EnableFlexibleCapacityReportingService]: boolean;
    [Feature.EnableBusinessUnitGroup]: boolean;
    [Feature.EnableJobTypeGroup]: boolean;
    [Feature.CapacityReportingServiceMFEUrlOverride]: string;
    [Feature.EnableFlexibleCapacityScheduler]: boolean;
    [Feature.CapacitySchedulerMFEUrlOverride]: string;
    [Feature.EnableFlexibleCapacitySettings]: boolean;
    [Feature.CapacitySettingsMFEUrlOverride]: string;
    [Feature.DispatchProConsoleMFEUrl]: string;
    [Feature.DispatchProConsoleSettingsMFEUrl]: string;
    [Feature.RecalculateReplenishmentAction]: boolean;
    [Feature.EnableSurveyActivationOnboarding]: boolean;
    [Feature.EnableGoogleReviewUpdateHandling]: boolean;
    [Feature.EnableSurveyDeliveryFeedback]: boolean;
    [Feature.EnableReputationROIReporting]: boolean;
    [Feature.EnableListingFieldsBatch3]: boolean;
    [Feature.EnablePricebookUnmapItems]: boolean;
    [Feature.EnableARManagementByBusinessUnitsTab]: boolean;
    [Feature.TransfersToJobs]: boolean;
    [Feature.AutoAssignTruckToJobTransfers]: boolean;
    [Feature.EnablePricebookPro]: boolean;
    [Feature.EnableRddataRemoteDatasourceRepository]: boolean;
    [Feature.EnableOtherDirectCosts]: boolean;
    [Feature.EnableDispatchActivityCenter]: boolean;
    [Feature.EnableUpdatedDispatchMessaging]: boolean;
    [Feature.BudgetVsActualHoursOnJob]: boolean;
    [Feature.DispatchMessagingMicroFrontendUrl]: string;
    [Feature.DispatchNonJobEventsMicroFrontEndUrl]: string;
    [Feature.EnableEnhancedWorkOrder]: string;
    [Feature.Countries]: string;
    [Feature.EnableDirectMailPropertyOwner]: boolean;
    [Feature.EnableMarketingStandaloneDataMigration]: boolean;
    [Feature.ImportBuiltInCustomReportsFromRdAdmin]: boolean;
    [Feature.EnablePartiallyReceivedForCosting]: boolean;
    [Feature.EnableSalesPro]: boolean;
    [Feature.EnableFieldAssistPro]: boolean;
    [Feature.EnableAudienceMembersExportBackgroundProcess]: boolean;
    [Feature.InternalExternalCommunicationUsesCommHub]: boolean;
    [Feature.DisablePurchasingApprovalWorkflow]: boolean;
    [Feature.PricebookItemsMaxCount]: number;
    [Feature.ProjectJobTaskTemplates]: boolean;
    [Feature.BudgetSegments]: boolean;
    [Feature.EnableUserAndRolePermissionsAuditTrailUserInterface]: boolean;
    [Feature.EnableAllowManageUserSessions]: boolean;
    [Feature.FleetProCookieBasedAuthEnabled]: boolean;
    [Feature.EnableCustomerStatementCustomization]: boolean;
    [Feature.EnableThirdPartyIntegration]: boolean;
    [Feature.DocumentsDocumentManagementEnabled]: boolean;
    [Feature.DocumentManagementMigration]: DocumentManagementMigrationStatus;
    [Feature.MfaEnforcementPolicy]: MfaEnforcementPolicy;
    [Feature.EnableServerSidePaginationForPaymentCollections]: boolean;
    [Feature.EnableCrewsScheduling]: boolean;
    [Feature.CrewsSchedulingMicroFrontendUrl]: string;
    [Feature.FmaTaskingUx]: boolean;
    [Feature.PrepopulateInvoiceDate]: boolean;
    [Feature.EnableCreditMemos]: boolean;
    [Feature.EnablePayThroughServiceTitanForPayables]: boolean;
    [Feature.EnableOrgStructure]: boolean;
    [Feature.OrgStructureApiUrl]: string;
    [Feature.ProjectPlan]: boolean;
    [Feature.BulkScheduleRescheduleAppts]: boolean;
    [Feature.EnableVendorPortal]: boolean;
    [Feature.EnableSync2]: boolean;
    [Feature.PreventSendingAdditionalRequestOnEventChange]: boolean;
    [Feature.EnableEnterpriseHub]: boolean;
    [Feature.EnableProjectAccounting]: boolean;
    [Feature.AccountingMfeUrlOverrideJournalEntries]: string | null;
    [Feature.AccountingMfeUrlOverrideGlService]: string | null;
    [Feature.EnableExteriorsProjectsAndJobs]: boolean;
    [Feature.SalesProSettingsMFEUrl]: string;
    [Feature.EnableSalesProSettings]: boolean;
    [Feature.AllowFeesOnDeposits]: boolean;
    [Feature.AudienceRelativeDateWithTotalDays]: boolean;
    [Feature.EnableGlBasedJobCosting]: boolean;
    [Feature.OverrideMarketingSmsCampaignsAvailability]: boolean;
    [Feature.EnableConfigurableSkuCost]: boolean;
    [Feature.RevocationConfig]: string;
    [Feature.ManualJournalEntries]: boolean;
    [Feature.EnableARBalanceRecon]: boolean;
    [Feature.RfiAndChangeOrderRequestsOnProjects]: boolean;
    [Feature.EnableTaskHub]: boolean;
    [Feature.EnableProductionQueue]: boolean;
    [Feature.EnableExternalPaymentsDashboard]: boolean;
    [Feature.HomhMarketplace]: boolean;
    [Feature.EnableLegacyAudiencesInMarketingStandalone]: boolean;
    [Feature.EnableDteAdvancedFiltering]: boolean;
    [Feature.ESignTemplateTypes]: string;
    [Feature.EnableHtmlEditing]: boolean;
    [Feature.CustomerJourneyEnableDoceboLms]: boolean;
    [Feature.AllowTechnicianTipping]: boolean;
    [Feature.EnableTrueJobAppointmentDuration]: boolean;
    [Feature.EnableSalesProDesktop]: boolean;
    [Feature.SalesProDesktopMFEUrl]: string;
    [Feature.DispatchProSupervisorConsoleMFEUrl]: string;
};

export interface IFeaturesStore {
    features: Features;
}

@injectable()
export class FeaturesStore extends Store implements IFeaturesStore {
    @observable features!: Features;

    constructor(@inject(FeaturesApi) private readonly api: FeaturesApi) {
        super();
        makeObservable(this);
    }

    async initialize() {
        const result = (await this.api.get()).data;

        runInAction(() => {
            this.features = result;
        });
    }
}
